/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useState, useEffect } from "react";
import Container from "../../components/Container";
import NavigationHeader from "../../components/NavigationHeader";
import { ROUTES } from "../../constants/constants";
import usePluginUtils from "../../hooks/usePluginUtils";
import { usePostClientPasswordByHash } from "../../lib/react-query/usePostClientPasswordByHash";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useToast } from "../../components/ui/Toast";
import styled from "styled-components";
import useWidgetCallbackParams from "../../hooks/useWidgetCallbackParams";
import Form from "../../components/ui/form/Form";
import FormFieldsWrapper from "../../components/ui/form/FormFieldsWrapper";
import FormGroup from "../../components/ui/form/FormGroup";
import FormError from "../../components/ui/form/FormError";
import PasswordInput from "../../components/ui/form/PasswordInput";
import Button from "../../components/ui/Button";
import { useAuth } from "../../hooks/useAuth";
import Icon from "../../components/ui/Icon";
import Text from "../../components/ui/Text";
import { extractErrorMessage, extractFirstFieldError } from "../../utils/api/apiErrors";

const StyledRestorePasswordView = styled.div`
    display: grid;
    gap: 24px;
    place-items: start;
    padding: 24px;
    border: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
    border-radius: 8px;
`;

const StyledInvalidTokenViewInfo = styled.div`
    display: flex;
    width: 100%;
    gap: 8px;
    padding: 8px;
    border-radius: 4px;
    background-color: rgba(${({ theme }) => theme.colors.warning.rgb}, 0.1);
`;

const StyledErrorIcon = styled((props) => <Icon.Error {...props} />)`
    flex-shrink: 0;
`;

const ClientRestorePassword = () => {
    const navigate = useNavigate();
    const { isPluginEnabled } = usePluginUtils();
    const { mutateAsync: mutatePassword, isPending: isPasswordPending } = usePostClientPasswordByHash();
    const { loginByHash, isLoginByHashPending } = useAuth();
    const { hash, clearParams } = useWidgetCallbackParams();
    const { toast } = useToast();
    const isLocationsEnabled = isPluginEnabled("location");
    const isPending = isPasswordPending || isLoginByHashPending;
    const [showInvalidTokenView, setShowInvalidTokenView] = useState(false);
    const {
        handleSubmit,
        watch,
        control,
        formState: { errors },
        setError,
    } = useForm({
        defaultValues: {
            password: "",
            password_confirm: "",
        },
    });

    useEffect(() => {
        clearParams();
    }, [clearParams]);

    const onSubmit = async (data) => {
        const { password } = data;

        try {
            await mutatePassword({ password, hash });
            await loginByHash({ hash });

            navigate(isLocationsEnabled ? ROUTES.LOCATIONS : ROUTES.HOME);
            toast({
                title: "success",
                description: "Your password has been successfully updated",
                variant: "success",
            });
        } catch (error) {
            handleError(error, setError);
        }
    };

    const handleError = (error, setError) => {
        if (!setError) return;

        if (error.response?.status === 404) {
            setShowInvalidTokenView(true);

            return;
        }

        const passwordError = extractFirstFieldError(error, "password");

        if (passwordError) {
            setError("password", { message: passwordError });
        } else {
            setError("root", { message: extractErrorMessage(error, "An unexpected error occurred") });
        }
    };

    return (
        <>
            {showInvalidTokenView && (
                <Container>
                    <StyledRestorePasswordView>
                        <StyledInvalidTokenViewInfo>
                            <StyledErrorIcon color="warning" size={Icon.SIZE.SM} />
                            <Text color="dark" fontStyle="italic" size="small">The recovery link you selected has already been used or expired. Re-submit your email address to receive a new recovery link</Text>
                        </StyledInvalidTokenViewInfo>
                        <Button variant="primary" title="Return to Home page" onClick={() => navigate(ROUTES.HOME)} fullWidth />
                    </StyledRestorePasswordView>
                </Container>
            )}
            {!showInvalidTokenView && (
                <Container>
                    <NavigationHeader title="Set a new password" />
                    <StyledRestorePasswordView>
                        <Form>
                            <FormFieldsWrapper>
                                <FormGroup>
                                    <PasswordInput
                                        control={control}
                                        name="password"
                                        label="New password"
                                        placeholder="Enter your new password"
                                        error={errors.password}
                                        required={true}
                                    />
                                    {errors.password && <FormError name="password">{errors.password.message}</FormError>}
                                </FormGroup>
                                <FormGroup>
                                    <PasswordInput
                                        control={control}
                                        name="password_confirm"
                                        label="Repeat new password"
                                        placeholder="Enter your new password again"
                                        validationRules={{ validate: (value) => value === watch("password") || "Passwords do not match" }}
                                        error={errors.password_confirm}
                                        required={true}
                                    />
                                    {errors.password_confirm && <FormError name="password_confirm">{errors.password_confirm.message}</FormError>}
                                </FormGroup>
                            </FormFieldsWrapper>
                            <Button
                                variant="primary"
                                title="Save & Sign In"
                                onClick={handleSubmit(onSubmit)}
                                loading={isPending}
                            />
                            {errors.root && <FormError>{errors.root.message}</FormError>}
                        </Form>
                    </StyledRestorePasswordView>
                </Container>
            )}
        </>
    );
};

export default ClientRestorePassword;